import { AdminUserDraft, SSO_FIELDS } from 'Interfaces/Admin';

/**
 *  Validation for the user form, ported from UserView.save. Kept separate from
 *  the component so the rules can be tested without rendering, and so the order
 *  of the checks — which decides which message an admin actually sees — is
 *  visible in one place.
 */

export interface ValidationFailure {
  /** Localization key of the message to show. */
  message: string;
  /** Field to focus, matching the legacy behaviour. */
  focus?: 'userName' | 'email' | 'password' | 'confirmPassword' | 'groupId' | 'phoneNumber';
}

const EMAIL_RE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (value: string | string[] | null | undefined): boolean => {
  if (typeof value === 'string') {
    return EMAIL_RE.test(value.trim());
  }
  if (Array.isArray(value)) {
    return value.every((v) => EMAIL_RE.test(v.trim()));
  }
  return true;
};

export const usesSso = (user: AdminUserDraft): boolean =>
  SSO_FIELDS.some((field) => user[field] === true);

/** Roles that must have an email address on file. */
const ROLES_REQUIRING_EMAIL = ['admin,modeler', 'ordinary', 'modeler'];

export interface ValidationContext {
  /** True when the account is configured to create users without passwords. */
  adminDisablePassword: boolean;
  /** True for an existing user; a password may then be left blank to keep it. */
  isExisting: boolean;
}

/**
 *  A password that is long enough but not complex enough only warrants a
 *  confirmation ("save anyway"), not a refusal.
 */
export const isWeakPassword = (password: string): boolean =>
  !/[A-Z]/.test(password) || !/[a-z]/.test(password)
  || !/\d/.test(password) || !/[\W_]/.test(password);

/** Username, group and phone number. */
const validateIdentity = (user: AdminUserDraft): ValidationFailure | null => {
  const sso = usesSso(user);
  const email = (user.email || '').trim();

  if (!user.userName && !sso && !email) {
    return { message: 'fillUserName', focus: 'userName' };
  }
  if (!user.groupId) {
    return { message: 'selectUserGroup', focus: 'groupId' };
  }
  if (user.phoneNumber && !/^\+?\d+$/.test(user.phoneNumber)) {
    return { message: 'phoneNumberInvalid', focus: 'phoneNumber' };
  }
  return null;
};

/**
 *  Password rules, skipped entirely when the user signs in through an identity
 *  provider, is being invited by email, or the account creates users without
 *  passwords at all.
 */
const validatePassword = (
  user: AdminUserDraft, password: string, confirmPassword: string, ctx: ValidationContext,
): ValidationFailure | null => {
  const invited = user.sendInvitationEmail === true;
  const exempt = usesSso(user) || invited || ctx.adminDisablePassword;

  if (!exempt) {
    if (!ctx.isExisting && !password) {
      return { message: 'fillInPassword', focus: 'password' };
    }
    if (!ctx.isExisting && !confirmPassword) {
      return { message: 'fillInConfirmationPass', focus: 'confirmPassword' };
    }
    if (password && password !== confirmPassword) {
      return { message: 'passwordNotMatch', focus: 'password' };
    }
    // An existing user may leave the field blank to keep the current password.
    if ((!ctx.isExisting || password) && password.length < 8) {
      return { message: 'passwordShort', focus: 'password' };
    }
  }
  if (ctx.adminDisablePassword && !invited && !ctx.isExisting) {
    return { message: 'emailForNoPassword', focus: 'email' };
  }
  return null;
};

/** Where an email address is mandatory, and whether the ones given are usable. */
const validateEmail = (user: AdminUserDraft): ValidationFailure | null => {
  const email = (user.email || '').trim();

  if ((usesSso(user) || user.sendInvitationEmail === true) && !email) {
    return { message: 'emailForSSO', focus: 'email' };
  }
  if (!email && ROLES_REQUIRING_EMAIL.includes(user.roles || '')) {
    return { message: 'emailForRole', focus: 'email' };
  }
  if (email && !isValidEmail(email)) {
    return { message: 'invalidEmail', focus: 'email' };
  }
  if (!email && (user.newDataNotification || user.updatedDataNotification
    || user.updatedCreatedDataNotification)) {
    return { message: 'emailForNotifications', focus: 'email' };
  }

  const reportTo = (user.sendReportTo || '').split(',').map((v) => v.trim()).filter(Boolean);
  if (!isValidEmail(reportTo)) {
    return { message: 'validSendTo', focus: 'email' };
  }
  return null;
};

/**
 *  Runs the checks in the order UserView.save did, since the first failure is
 *  the message the admin actually sees.
 */
export const validateUser = (
  user: AdminUserDraft,
  password: string,
  confirmPassword: string,
  ctx: ValidationContext,
): ValidationFailure | null =>
  validateIdentity(user)
  || validatePassword(user, password, confirmPassword, ctx)
  || validateEmail(user);

/**
 *  Normalises the draft the way UserView.save did just before handing it to the
 *  server: single sign-on users are identified by their email and carry no
 *  local credentials, and office roles report to their own address by default.
 */
export const prepareUserPayload = (
  user: AdminUserDraft, password: string, isExisting: boolean,
): AdminUserDraft => {
  const payload: AdminUserDraft = { ...user, password };
  const email = (payload.email || '').trim().toLowerCase();
  payload.email = email;
  payload.userName = (payload.userName || '').toLowerCase();

  if (payload.roles === 'admin') {
    payload.roles = 'admin,modeler';
  }
  if (usesSso(payload)) {
    payload.userName = email;
    payload.fullName = '';
    payload.password = '';
  }
  if (!payload.userName) {
    payload.userName = email;
  }
  if (!isExisting && email && !payload.sendReportTo
    && ROLES_REQUIRING_EMAIL.includes(payload.roles || '')) {
    payload.sendReportTo = email;
  }
  return payload;
};
